import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  applicationsApplicationsListOptions,
  organizationsRolesListOptions
} from 'src/api/main'

/**
 * Roles and applications are bounded per organization (tens, not thousands),
 * so both pickers load in one page. `truncated` below surfaces the case where
 * that assumption stops holding instead of silently cutting the list.
 */
const PAGE_SIZE = 200

export type GroupRoleOptionGroup = {
  label: string
  options: { value: number; label: string }[]
}

export const useApplicationNameMap = (workspaceId: number, enabled = true) => {
  const { data, isLoading } = useQuery({
    ...applicationsApplicationsListOptions({
      query: { organization: workspaceId, per_page: PAGE_SIZE }
    }),
    enabled: enabled && Number.isFinite(workspaceId)
  })

  const appNameById = useMemo(
    () => new Map((data?.results ?? []).map((app) => [app.id, app.name])),
    [data]
  )

  return { appNameById, isLoading }
}

export const useGroupRoleOptions = (workspaceId: number, enabled = true) => {
  const { t } = useTranslation()
  const active = enabled && Number.isFinite(workspaceId)

  const { data, isLoading: rolesLoading } = useQuery({
    ...organizationsRolesListOptions({
      query: { organization: workspaceId, per_page: PAGE_SIZE }
    }),
    enabled: active
  })

  const { appNameById, isLoading: appsLoading } = useApplicationNameMap(
    workspaceId,
    active
  )

  const roles = useMemo(() => data?.results ?? [], [data])

  const roleGroups = useMemo<GroupRoleOptionGroup[]>(() => {
    const byApp = new Map<number | null, { value: number; label: string }[]>()

    roles.forEach((role) => {
      const applicationId = role.application ?? null
      const bucket = byApp.get(applicationId) ?? []
      bucket.push({ value: role.id, label: role.name })
      byApp.set(applicationId, bucket)
    })

    return Array.from(byApp.entries())
      .map(([applicationId, options]) => ({
        applicationId,
        label:
          applicationId == null
            ? t('users.groups.organizationScope')
            : (appNameById.get(applicationId) ?? `#${applicationId}`),
        options
      }))
      .sort((a, b) => {
        if (a.applicationId == null) return -1
        if (b.applicationId == null) return 1

        return a.label.localeCompare(b.label)
      })
      .map(({ label, options }) => ({ label, options }))
  }, [appNameById, roles, t])

  const scopeByRoleId = useMemo(() => {
    const map = new Map<number, string | null>()
    roles.forEach((role) => {
      const applicationId = role.application ?? null
      map.set(
        role.id,
        applicationId == null
          ? null
          : (appNameById.get(applicationId) ?? `#${applicationId}`)
      )
    })

    return map
  }, [appNameById, roles])

  return {
    roleGroups,
    scopeByRoleId,
    isLoading: rolesLoading || appsLoading,
    truncated: (data?.count ?? 0) > roles.length
  }
}
